import { useCallback, useState } from 'react';
import _ from 'lodash';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Input } from 'components/ui/input';
import SuccessBox from 'components/SuccessBox';
import LanguageSelector from 'components/LanguageSelector';

import { sendForgotPasswordEmail } from 'entities/Auth/sdk';
import { Button } from 'components/ui/button.tsx';

const ResendResetLinkForm = ({ email }: { email: string }) => {
  const { t } = useTranslation();

  const [resetLinkSent, setResetLinkSent] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formErrors, setErrors] = useState({ errors: {}, message: '' });

  const resendLink = useCallback(() => {
    setIsSubmitting(true);
    sendForgotPasswordEmail({ email })
      .then(() => {
        setResetLinkSent(true);
      })
      .catch((err) => {
        err.response?.text()?.then((data: string) => setErrors(JSON.parse(data)));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [email]);

  return (
    <>
      {resetLinkSent && (
        <div className={'flex flex-col gap-[24px]'}>
          <SuccessBox text={t('resetPassword.successTitle')}>
            <div className='flex flex-col gap-[8px]'>
              <div className='flex gap-[8px]'>
                <InformationCircleIcon className='size-[20px] text-content-secondary' />
                <div className='flex-1 font-[MuseoSansCyrl] text-[14px]'>{t('resetPassword.linkSentTitle')}</div>
              </div>
              <div className='flex gap-[8px]'>
                <InformationCircleIcon className='size-[20px] text-content-secondary' />
                <div className='flex-1 font-[MuseoSansCyrl] text-[14px] max-w-[400px]'>
                  {t('resetPassword.linkSentDescription')}
                </div>
              </div>
            </div>
          </SuccessBox>
          <Button onClick={resendLink} className={'self-center w-full md:w-fit font-semibold'} disabled={isSubmitting}>
            {t('resetPassword.resendResetLinkButton')}
          </Button>
        </div>
      )}
      {!resetLinkSent && (
        <>
          <h1 className='bold text-grey800 font-[DKLiquidEmbrace] text-3xl pb-[24px] text-center md:text-left'>
            {t('resetPassword.title')}
          </h1>
          <div className='w-full flex justify-end'>
            <LanguageSelector
              hoverStyle='bg-brand-yellow hover:bg-brand-yellow data-[popup-open]:bg-brand-yellow/60'
              mobileStyle='text-brand-brown'
            />
          </div>
          {!_.isEmpty(formErrors.errors) && (
            <div className='text-danger pb-[10px]'> {_.map(formErrors?.errors, (value: string) => value)}</div>
          )}
          {_.isEmpty(formErrors.errors) && formErrors.message !== '' && (
            <div className='text-danger pb-[10px]'>{formErrors.message}</div>
          )}
          <div className={'flex flex-col gap-[24px]'}>
            <Input label={t('common.emailLabel')} value={email} disabled={true} />
            <Button onClick={resendLink} className={'w-full md:w-fit font-semibold'} disabled={isSubmitting}>
              {t('resetPassword.resendLinkButton')}
            </Button>
          </div>
        </>
      )}
    </>
  );
};

export default ResendResetLinkForm;
